import {
  deleteDoc,
  doc,
  GeoPoint,
  getDoc,
  onSnapshot,
  setDoc,
  type Unsubscribe,
} from 'firebase/firestore'
import { AuthenticationService } from '@core/services/authentication/authentication-service'
import { firestore } from '@core/services/firebase/firebase-service'
import { CoreError, CoreErrorCause } from '@domain/models/core-error'
import type { UserProfile } from '@domain/models/user'
import { UserConverter } from '@core/services/user/converters/user-converter'
import { UserErrorCause, UserErrorConverter } from '@core/services/user/converters/user-error-converter'

const USERS_COLLECTION = 'users'

function normalizeEmail(email: string) {
  return email.trim().toLowerCase()
}

function readString(
  data: Record<string, unknown>,
  ...keys: string[]
): string | undefined {
  for (const key of keys) {
    if (typeof data[key] === 'string') {
      return data[key] as string
    }
  }
  return undefined
}

function readBoolean(data: Record<string, unknown>, ...keys: string[]): boolean {
  for (const key of keys) {
    if (typeof data[key] === 'boolean') {
      return data[key] as boolean
    }
  }
  return false
}

function toMapLocation(
  value: unknown,
): { latitude: number; longitude: number } | null | undefined {
  if (value === null) {
    return null
  }
  if (value instanceof GeoPoint) {
    return {
      latitude: value.latitude,
      longitude: value.longitude,
    }
  }
  if (
    value &&
    typeof value === 'object' &&
    'latitude' in value &&
    'longitude' in value
  ) {
    const mapLocation = value as { latitude?: unknown; longitude?: unknown }
    if (
      typeof mapLocation.latitude === 'number' &&
      typeof mapLocation.longitude === 'number'
    ) {
      return {
        latitude: mapLocation.latitude,
        longitude: mapLocation.longitude,
      }
    }
  }
  return undefined
}

function getUsersDoc(uid: string) {
  if (!firestore) {
    throw UserErrorConverter.domainModel(
      new Error('Firestore is not initialized'),
    )
  }
  return doc(firestore, USERS_COLLECTION, uid)
}

export class UserService {
  private static instance: UserService

  private constructor() {}

  public static getInstance(): UserService {
    if (!UserService.instance) {
      UserService.instance = new UserService()
    }
    return UserService.instance
  }

  public async createUserDocument(uid: string, email: string): Promise<UserProfile> {
    try {
      await setDoc(
        getUsersDoc(uid),
        {
          uid,
          email: normalizeEmail(email),
        },
        { merge: true },
      )

      return {
        uid,
        email: normalizeEmail(email),
      }
    } catch (error) {
      throw UserErrorConverter.domainModel(error)
    }
  }

  public async loadUser(uid: string): Promise<UserProfile> {
    try {
      const snapshot = await getDoc(getUsersDoc(uid))
      if (!snapshot.exists()) {
        throw UserErrorConverter.createUserError(
          UserErrorCause.UserNotFound,
          `User with uid ${uid} does not exist.`,
        )
      }

      return UserConverter.fromFirestore(
        snapshot.id,
        snapshot.data() as Record<string, unknown>,
      )
    } catch (error) {
      throw UserErrorConverter.domainModel(error)
    }
  }

  public getAuthenticatedUidOrThrow(): string {
    const uid = AuthenticationService.getInstance().userId
    if (!uid) {
      throw new CoreError({
        cause: CoreErrorCause.Forbidden,
        context: 'No authenticated user available for user service.',
      })
    }
    return uid
  }

  public async loadCurrentUserProfile(uid: string): Promise<UserProfile> {
    try {
      const snapshot = await getDoc(getUsersDoc(uid))
      if (!snapshot.exists()) {
        throw UserErrorConverter.createUserError(
          UserErrorCause.UserNotFound,
          `User profile with uid ${uid} does not exist.`,
        )
      }

      const data = snapshot.data() as Record<string, unknown>
      const profile = UserConverter.fromFirestore(snapshot.id, data)

      return {
        ...profile,
        admin: readBoolean(data, 'admin', 'isAdmin'),
        isAdmin: readBoolean(data, 'isAdmin', 'admin'),
        profile_slug: readString(data, 'profile_slug', 'fullName'),
        photo_url: readString(data, 'photo_url', 'avatarUrl'),
        public_full_name: readString(data, 'public_full_name'),
        short_bio: readString(data, 'short_bio'),
        public_contact_email: readString(data, 'public_contact_email'),
        map_location: toMapLocation(data['map_location']),
      }
    } catch (error) {
      throw UserErrorConverter.domainModel(error)
    }
  }

  public async loadAuthenticatedCurrentUserProfile(): Promise<UserProfile> {
    const uid = this.getAuthenticatedUidOrThrow()
    return this.loadCurrentUserProfile(uid)
  }

  public async saveUser(user: UserProfile): Promise<void> {
    try {
      await setDoc(getUsersDoc(user.uid), UserConverter.toFirestore(user), {
        merge: true,
      })
    } catch (error) {
      throw UserErrorConverter.domainModel(error)
    }
  }

  public async updateUser(uid: string, updates: Partial<UserProfile>): Promise<void> {
    try {
      const payload = UserConverter.partialToFirestore(updates)
      if (Object.keys(payload).length === 0) {
        return
      }

      await setDoc(getUsersDoc(uid), payload, { merge: true })
    } catch (error) {
      throw UserErrorConverter.domainModel(error)
    }
  }

  public async updateUserName(uid: string, fullName: string): Promise<void> {
    await this.updateUser(uid, { fullName })
  }

  public async updateUserProfilePhoto(uid: string, avatarUrl: string): Promise<void> {
    await this.updateUser(uid, { avatarUrl })
  }

  public observeUser(
    uid: string,
    callback: (user: UserProfile | null) => void,
    onError?: (error: Error) => void,
  ): Unsubscribe {
    return onSnapshot(
      getUsersDoc(uid),
      (snapshot) => {
        if (!snapshot.exists()) {
          callback(null)
          return
        }

        callback(
          UserConverter.fromFirestore(
            snapshot.id,
            snapshot.data() as Record<string, unknown>,
          ),
        )
      },
      (error) => {
        onError?.(UserErrorConverter.domainModel(error))
      },
    )
  }

  public async deleteUser(uid: string): Promise<void> {
    try {
      await deleteDoc(getUsersDoc(uid))
    } catch (error) {
      throw UserErrorConverter.domainModel(error)
    }
  }
}

const userService = UserService.getInstance()

export function getAuthenticatedUidOrThrow(): string {
  return userService.getAuthenticatedUidOrThrow()
}

export async function loadCurrentUserProfile(uid: string): Promise<UserProfile> {
  return userService.loadCurrentUserProfile(uid)
}

export async function loadAuthenticatedCurrentUserProfile(): Promise<UserProfile> {
  return userService.loadAuthenticatedCurrentUserProfile()
}

export async function deleteUser(uid: string): Promise<void> {
  return userService.deleteUser(uid)
}
